import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Copy, Wallet, Users, Clock, BadgeCheck, Award, Ticket, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { getUser } from '@/utils/auth';

/** Consumer Referral dashboard (Phase 3 — V5.0). Route: /referrals */
export default function ReferralDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const userId = user?.user_id || user?.id;
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [rRes, cRes] = await Promise.all([
        apiClient.get('/referrals/my', { params: { user_id: userId, user_type: 'consumer' } }),
        apiClient.get('/coupons/my', { params: { user_id: userId } }),
      ]);
      setData(rRes.data);
      setCoupons(cRes.data.coupons || []);
    } catch { toast.error('Failed to load referrals'); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    if (!userId) { navigate('/login?redirect=/referrals'); return; }
    load();
  }, [userId, load, navigate]);

  const shareUrl = data ? `${window.location.origin}/register?ref=${data.code}` : '';

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Join ZenStudios', text: `Use my code ${data.code}`, url: shareUrl }); } catch { /* cancelled */ }
    } else {
      copy(shareUrl, 'Referral link');
    }
  };

  if (loading || !data) {
    return <div className="min-h-[50vh] flex items-center justify-center text-gray-400" data-testid="referral-loading">Loading…</div>;
  }

  const Stat = ({ icon: Icon, label, value, tone }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
      <Icon className={`mx-auto mb-1 ${tone || 'text-gray-500'}`} size={18} />
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 space-y-8" data-testid="referral-dashboard">
      <div>
        <h1 className="text-3xl font-semibold flex items-center gap-2"><Gift className="text-amber-500" /> Refer &amp; Earn</h1>
        <p className="text-gray-500 mt-1">Share your code. You're rewarded when a friend makes their first payment.</p>
      </div>

      {/* Code + share */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white p-6">
        <div className="text-sm opacity-90">Your referral code</div>
        <div className="flex items-center gap-3 mt-1">
          <div className="text-3xl font-bold font-mono tracking-wider" data-testid="referral-code">{data.code}</div>
          <button onClick={() => copy(data.code, 'Code')} data-testid="referral-copy-code" className="bg-white/20 rounded-lg p-2 hover:bg-white/30"><Copy size={18} /></button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <input readOnly value={shareUrl} className="flex-1 min-w-[200px] bg-white/15 rounded-lg px-3 py-2 text-sm" data-testid="referral-share-url" />
          <button onClick={() => copy(shareUrl, 'Referral link')} data-testid="referral-copy-link" className="bg-white text-orange-600 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"><Copy size={15} /> Copy</button>
          <button onClick={share} data-testid="referral-share-btn" className="bg-white/20 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"><Share2 size={15} /> Share</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat icon={Users} label="Total" value={data.stats.total} />
        <Stat icon={Clock} label="Pending" value={data.stats.pending} tone="text-amber-500" />
        <Stat icon={BadgeCheck} label="Qualified" value={data.stats.qualified} tone="text-sky-500" />
        <Stat icon={Award} label="Rewarded" value={data.stats.rewarded} tone="text-emerald-500" />
        <Stat icon={Wallet} label="Wallet ₹" value={Math.round(data.wallet_balance || 0)} tone="text-emerald-600" />
      </div>

      {/* Reward coupons */}
      {coupons.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Ticket size={18} /> Your reward coupons</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {coupons.map((c) => (
              <div key={c.coupon_id} className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 flex items-center justify-between" data-testid={`reward-coupon-${c.code}`}>
                <div>
                  <div className="font-mono font-semibold text-amber-700">{c.code}</div>
                  <div className="text-xs text-gray-600">{c.business_unit_label} · {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Referral activity</h2>
        {data.events.length === 0 ? (
          <p className="text-gray-400 text-sm">No referrals yet — share your code to get started!</p>
        ) : (
          <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="referral-events">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr><th className="text-left px-4 py-2">Friend</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Date</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.events.map((e) => (
                  <tr key={e.event_id}>
                    <td className="px-4 py-2 text-gray-600">{(e.referred_user_id || '').slice(0, 12)}…</td>
                    <td className="px-4 py-2 capitalize">{e.status}</td>
                    <td className="px-4 py-2 text-gray-400">{(e.created_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
